import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import DisplayImage from '@/components/Ui-components/display-image';
import SectionHeader from '@/components/Common/SectionHeader';
import {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import {
    colors,
    borderRadius,
    borderWidth,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    shadows,
    spacing,
    transition
} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle, iconTileSx} from '@/styles/iconTile';
import Eyebrow from '@/components/Common/Eyebrow';

export interface CostTableRow {
    projectType: string;
    estimatedCost: string;
    /** Unused by the 2-column variant (`columns={2}`). */
    timeline?: string;
    /** Unused by the 2- and 3-column variants. */
    keyDrivers?: string;
}

export interface CostDriverCard {
    icon: StaticImageData | string;
    title: string;
    description: string;
}

export interface CostBreakdownProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    introText?: string[];
    quickOverview?: {label: string; price: string}[];
    tableHeaders?: {col1: string; col2: string; col3?: string; col4?: string};
    tableRows: CostTableRow[];
    /** Heading above the driver cards. Only needed when `driverCards` is supplied. */
    driversSectionHeading?: string;
    /** Omit entirely for a table-only section — the whole driver block is then skipped. */
    driverCards?: CostDriverCard[];
    buttonText?: string;
    onButtonClick?: () => void;
    /** Render a 3-column table (Service / Range / Timeline) instead of the default 4 */
    hideKeyDrivers?: boolean;
    /**
     * Explicit column count, overriding `hideKeyDrivers`. Use `2` for a factor/description table:
     * the second column then holds prose, so it renders in the regular body style rather than the
     * emerald price style used by the cost variants.
     */
    columns?: 2 | 3 | 4;
    /** Where the driver cards render relative to the table. Defaults to 'below'. */
    driversPosition?: 'above' | 'below';
    /** Number of driver cards per row on desktop. Defaults to 3. */
    driverColumns?: 2 | 3 | 4;
    /** Optional highlighted note below the table, e.g. what drives the cost. Accepts inline HTML. */
    footerNote?: string;
}

const topGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
    gap: spacing[40],
    mb: spacing[48],
    alignItems: 'start'
} as const;

const introParagraphSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const quickCardSx = {
    bgcolor: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    overflow: 'hidden'
} as const;

const quickCardHeaderSx = {
    px: spacing[24],
    py: spacing[20],
    borderBottom: `${borderWidth.thin} solid ${colors.neutral.borderLight}`
} as const;

/** 4 prose columns stop being readable below this, so the table scrolls instead. */
const TABLE_MIN_WIDTH = 900;
/** 3-column variant (`hideKeyDrivers`) needs less room. */
const TABLE_MIN_WIDTH_NARROW = 720;
/** 2-column variant fits comfortably on a phone once it scrolls. */
const TABLE_MIN_WIDTH_TWO_COL = 560;

/** Column tracks per column count. */
const TABLE_GRID_TEMPLATES = {
    2: '2.4fr 3.6fr',
    3: '2.4fr 1.6fr 1.4fr',
    4: '2.2fr 1.6fr 1.2fr 2.6fr'
} as const;

const TABLE_MIN_WIDTHS = {
    2: TABLE_MIN_WIDTH_TWO_COL,
    3: TABLE_MIN_WIDTH_NARROW,
    4: TABLE_MIN_WIDTH
} as const;

/** Horizontal scroller — the table keeps the same columns at every width. */
const tableScrollSx = {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    mb: spacing[56]
} as const;

const tableWrapSx = {
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    overflow: 'hidden'
} as const;

const tableHeaderSx = {
    display: 'grid',
    gridTemplateColumns: '2.2fr 1.6fr 1.2fr 2.6fr',
    bgcolor: colors.primary.darkNavy,
    px: spacing[24],
    py: spacing[16],
    gap: spacing[16]
} as const;

const tableHeaderCellSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold,
    color: colors.text.inverse,
    letterSpacing: letterSpacing.mid,
    textTransform: 'uppercase' as const,
    lineHeight: lineHeight[20]
} as const;

const tableRowSx = {
    display: 'grid',
    gridTemplateColumns: '2.2fr 1.6fr 1.2fr 2.6fr',
    px: spacing[24],
    py: spacing[20],
    gap: spacing[16],
    borderBottom: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.default,
    alignItems: 'center',
    '&:last-child': {borderBottom: 'none'}
} as const;

const driverGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
    gap: spacing[32],
    mt: spacing[32]
} as const;

const driverCardSx = {
    bgcolor: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    p: spacing[32],
    display: 'flex',
    flexDirection: 'column' as const,
    transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.deep,
        boxShadow: shadows.lg,
        transform: 'scale(1.02)'
    }
} as const;

const iconBgSx = {
    ...iconTileSx,
    mb: spacing[24]
} as const;

const footerNoteSx = {
    mt: spacing[32],
    p: {xs: spacing[20], md: spacing[24]},
    bgcolor: colors.background.tag,
    border: `${borderWidth.thin} solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.lg,
    transition: `transform ${transition.base}, box-shadow ${transition.base}`,
    '&:hover': {
        transform: 'scale(1.01)',
        boxShadow: shadows.lg
    }
} as const;

const footerNoteTextSx = {
    fontSize: fontSize[16],
    color: colors.primary.deep,
    lineHeight: lineHeight[28]
} as const;

const buttonWrapSx = {
    display: 'flex',
    justifyContent: 'center',
    mt: spacing[48]
} as const;

const introColumnSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[20]
} as const;

const quickCardHeaderTitleSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const quickRowLabelSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

const quickRowPriceSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[20],
    whiteSpace: 'nowrap' as const,
    ml: spacing[16]
} as const;

const tableCellProjectSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const tableCellCostSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.status.success,
    lineHeight: lineHeight[20]
} as const;

const tableCellRegularSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

const driversHeadingSx = {
    fontSize: {xs: fontSize[24], md: fontSize[28]},
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[40]
} as const;

const driverTitleSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    mb: spacing[12]
} as const;

const driverDescSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const DEFAULT_TABLE_HEADERS = {
    col1: 'Project Type',
    col2: 'Estimated Cost',
    col3: 'Typical Timeline',
    col4: 'Key Drivers'
};

const CostBreakdown = ({
    eyebrow,
    heading,
    subheading,
    introText,
    quickOverview,
    tableHeaders = DEFAULT_TABLE_HEADERS,
    tableRows,
    driversSectionHeading,
    driverCards = [],
    buttonText,
    onButtonClick,
    hideKeyDrivers,
    columns,
    footerNote,
    driversPosition = 'below',
    driverColumns = 3
}: CostBreakdownProps) => {
    const hasTopSection = (introText && introText.length > 0) || (quickOverview && quickOverview.length > 0);
    const columnCount = columns ?? (hideKeyDrivers ? 3 : 4);
    const gridTemplateColumns = TABLE_GRID_TEMPLATES[columnCount];
    const minWidth = TABLE_MIN_WIDTHS[columnCount];
    const headerGridSx = {...tableHeaderSx, gridTemplateColumns, minWidth};
    const rowGridSx = {...tableRowSx, gridTemplateColumns, minWidth};
    // The 2-column variant's second column carries descriptions, not prices.
    const secondColumnSx = columnCount === 2 ? tableCellRegularSx : tableCellCostSx;

    const gridSx = {
        ...driverGridSx,
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${driverColumns}, 1fr)`},
        ...(driversPosition === 'above' ? {mb: spacing[56]} : {})
    };

    // Nothing after the table means its bottom margin would be dead space above the section padding.
    const hasContentBelowTable = Boolean(
        footerNote || (driversPosition === 'below' && driverCards.length) || (buttonText && onButtonClick)
    );

    // Sections that only need the table pass no driver cards — skip the block entirely
    // so an empty heading and grid don't add dead vertical space.
    const driversBlock = !driverCards.length ? null : (
        <Box>
            <Typography component="h3" className="font-headers" sx={driversHeadingSx}>
                {driversSectionHeading}
            </Typography>
            <Box sx={gridSx}>
                {driverCards.map((card) => (
                    <Box key={card.title} sx={driverCardSx}>
                        {card.icon && (
                            <Box sx={iconBgSx}>
                                <DisplayImage
                                    src={card.icon}
                                    alt={card.title}
                                    width={ICON_TILE_ICON_SIZE}
                                    height={ICON_TILE_ICON_SIZE}
                                    style={iconTileImageStyle}
                                />
                            </Box>
                        )}
                        <Typography component="h4" className="font-headers" sx={driverTitleSx}>
                            {card.title}
                        </Typography>
                        <Typography className="font-regular-text" sx={driverDescSx}>
                            {parse(card.description)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    return (
        <MUIContainer>
            <SectionHeader
                eyebrow={
                    eyebrow ? (
                        // Bespoke eyebrow: brand-blue/bold, distinct from the canonical slate `.eyebrow`.
                        <Eyebrow text={eyebrow} />
                    ) : undefined
                }
                heading={heading}
                subheading={parse(subheading)}
                subheadingVariant="body1"
            />

            {hasTopSection && (
                <Box sx={topGridSx}>
                    {introText && introText.length > 0 && (
                        <Box sx={introColumnSx}>
                            {introText.map((para, i) => (
                                <Typography key={i} className="font-regular-text" sx={introParagraphSx}>
                                    {para}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {quickOverview && quickOverview.length > 0 && (
                        <Box sx={quickCardSx}>
                            <Box sx={quickCardHeaderSx}>
                                <Typography sx={quickCardHeaderTitleSx}>Quick Cost Overview by Project Type</Typography>
                            </Box>
                            {quickOverview.map((row, i) => (
                                <Box
                                    key={row.label}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: spacing[24],
                                        py: spacing[12],
                                        borderBottom:
                                            i < quickOverview.length - 1
                                                ? `${borderWidth.thin} solid ${colors.neutral.borderLight}`
                                                : 'none'
                                    }}
                                >
                                    <Typography sx={quickRowLabelSx}>{row.label}</Typography>
                                    <Typography sx={quickRowPriceSx}>{row.price}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {driversPosition === 'above' && driversBlock}

            {/* tabIndex + role make the scrollable region reachable by keyboard. */}
            <Box
                sx={hasContentBelowTable ? tableScrollSx : {...tableScrollSx, mb: 0}}
                tabIndex={0}
                role="region"
                aria-label={`${heading} table`}
            >
                <Box sx={tableWrapSx}>
                    <Box sx={headerGridSx}>
                        <Typography className="font-headers" sx={tableHeaderCellSx}>
                            {tableHeaders.col1}
                        </Typography>
                        <Typography className="font-headers" sx={tableHeaderCellSx}>
                            {tableHeaders.col2}
                        </Typography>
                        {columnCount >= 3 && (
                            <Typography className="font-headers" sx={tableHeaderCellSx}>
                                {tableHeaders.col3}
                            </Typography>
                        )}
                        {columnCount >= 4 && (
                            <Typography className="font-headers" sx={tableHeaderCellSx}>
                                {tableHeaders.col4}
                            </Typography>
                        )}
                    </Box>
                    {tableRows.map((row) => (
                        <Box key={row.projectType} sx={rowGridSx}>
                            <Typography className="font-headers" sx={tableCellProjectSx}>
                                {row.projectType}
                            </Typography>
                            <Typography sx={secondColumnSx}>{row.estimatedCost}</Typography>
                            {columnCount >= 3 && <Typography sx={tableCellRegularSx}>{row.timeline}</Typography>}
                            {columnCount >= 4 && <Typography sx={tableCellRegularSx}>{row.keyDrivers}</Typography>}
                        </Box>
                    ))}
                </Box>
            </Box>

            {footerNote && (
                <Box sx={footerNoteSx}>
                    <Typography component="p" className="font-regular-text remove-underline" sx={footerNoteTextSx}>
                        {parse(footerNote)}
                    </Typography>
                </Box>
            )}

            {driversPosition === 'below' && driversBlock}

            {buttonText && onButtonClick && (
                <Box sx={buttonWrapSx}>
                    <MUIButton
                        label={buttonText}
                        aria-label={buttonText}
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowForwardIcon />}
                        disableRipple={true}
                        className="animated-button moving-icon-button button-bg-primary"
                        onClick={onButtonClick}
                    />
                </Box>
            )}
        </MUIContainer>
    );
};

export default memo(CostBreakdown);
